import './ButtonDropdown.scss';
import { PropType, h, defineComponent, ref, computed, onMounted } from 'vue';
import { has_class } from '@vaaas/fpts/dom';
import { on_next_frame } from '@vaaas/fpts/timing';
import { return_focus_to_scene } from '@phi/util/dom';
import { Vector2 } from 'three';
import { Direction, is_opposite_direction, move_direction } from '@phi/maths/vector2';

const is_disabled = has_class('disabled');

export type Toggle = (x: boolean) => void;

export default defineComponent({
    props: {
        direction: { required: true, type: String as PropType<Direction> },
        id: { required: true, type: String },
        disabled: { required: true, type: Boolean },
    },

    setup(props, context) {
        const { slots: { primary, dropdown } } = context;

        // data
        const root = ref<undefined | HTMLDivElement>(undefined);
        const buttons = ref<Array<HTMLButtonElement>>([]);
        const initial = new Vector2(0, 0);
        const latest = new Vector2(0, 0);

        // derived
        const button_count = computed(() => buttons.value.length);
        const disabled_count = computed(() => buttons.value.filter(is_disabled).length);
        const active = computed(() => button_count.value > disabled_count.value);
        const distance_between_current_and_latest = () => Math.abs(initial.x - latest.x) + Math.abs(initial.y - latest.y);
        const has_moved = () => distance_between_current_and_latest() > 25;

        // methods
        const show = () => root.value?.focus();
        const hide = () => return_focus_to_scene();

        const get_buttons = (): HTMLButtonElement[] => Array.from(root.value!.querySelectorAll('.dropdown button'));
        const update_activity = on_next_frame(() => buttons.value = get_buttons());
        const reset = () => {
            initial.x = initial.y = latest.x = latest.y = 0;
        };

        // event handlers
        const on_pointer_over = (x: PointerEvent) => {
            if (x.pointerType === 'mouse')
                show();
            else {
                initial.x = latest.x = x.clientX;
                initial.y = latest.y = x.clientY;
            }
        };
        const on_pointer_move = (x: PointerEvent) => {
            if (x.pointerType === 'mouse')
                return;

            latest.x = x.clientX;
            latest.y = x.clientY;
            if (has_moved()) {
                if (is_opposite_direction(move_direction(initial, latest), props.direction))
                    hide();
                else
                    show();
            }
        };
        const on_pointer_leave = (x: PointerEvent) => {
            if (x.pointerType === 'mouse')
                hide();
            else if (has_moved())
                reset();
            else
                hide();
        };

        // hooks
        onMounted(() => update_activity());

        return () => h('div', {
            tabindex: '0',
            ref: root,
            class: {
                'btn-group': true,
                ['drop' + props.direction]: true,
                'has-dropdown': true,
                'no-dash': props.disabled,
                'has-active': active.value
            },
            id: props.id,
            onPointerover: on_pointer_over,
            onPointerleave: on_pointer_leave,
            onPointermove: on_pointer_move,
        }, [
            primary && h('div', { class: 'primary' }, primary(update_activity)),
            dropdown && h('div', { class: 'dropdown' }, dropdown(update_activity)),
        ]);
    },
});
